import React, { useState } from "react";
import {
  Typography,
  Grid,
  Box,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  TextField,
  InputAdornment,
  IconButton,
  Divider,
  useTheme,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import {
  Inventory2 as InventoryIcon,
  Image as ImageIcon,
  ChromeReaderMode as CoverIcon,
  Shield as ShieldIcon,
  Widgets as ComponentsIcon,
  TrendingUp as SeoIcon,
  AutoAwesome as A1Icon,
  FormatQuote as TestimonialsIcon,
  PushPin as PinnedIcon,
  PushPinOutlined as UnpinnedIcon,
  Search as SearchIcon,
  YouTube as YouTubeIcon,
  Chat as ShareIcon,
  Mail as LetterIcon,
  Tv as TvIcon,
  Web as FooterIcon,
  NewReleases as UpcomingIcon,
  Email,
  NotificationsActive as NotificationIcon,
  AccessTime as SessionTimeoutIcon,
  VpnKeyOutlined as ApiApplicationsIcon,
  WorkOutline as JobsIcon,
  VisibilityOutlined as VisibilityIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import { alpha } from "@mui/material/styles";
import { RootState } from "../../store/Index";
import { usePermissions } from "../../hooks/usePermissions";
import { hasConfigurationAccess } from "../../constants/permissions";
import { tokens } from "../../theme";

const MotionCard = motion(Box);

interface ISettingsCardProps {
  title: string;
  description: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  navigateTo?: string;
  isSwitchable?: boolean;
  initialSwitchState?: boolean;
  onSwitchChange?: (checked: boolean) => void;
  category: string;
  thumbnail_type?: string;
  themeColor: string;
  itemCount: number;
  isPinned: boolean;
  onTogglePin: (e: React.MouseEvent) => void;
}

const SettingsCard: React.FC<ISettingsCardProps> = ({
  title,
  description,
  icon,
  disabled = false,
  navigateTo,
  isSwitchable = false,
  initialSwitchState = false,
  onSwitchChange,
  thumbnail_type,
  themeColor,
  itemCount,
  isPinned,
  onTogglePin,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [isOn, setIsOn] = useState(initialSwitchState);

  const primaryTextColor = theme.palette.mode === "dark" ? "#ffffff" : "#111827";
  const secondaryTextColor = theme.palette.mode === "dark" ? "rgba(255,255,255,0.7)" : "#64748B";
  const cardBg = theme.palette.mode === "dark" ? tokens(theme.palette.mode, reduxTheme).primary[900] : "#ffffff";

  const handleCardClick = () => {
    if (!disabled && navigateTo) {
      navigate(
        `${navigateTo}${
          thumbnail_type ? `?thumbnail_type=${thumbnail_type}` : ""
        }`,
      );
    }
  };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsOn(event.target.checked);
    if (onSwitchChange) {
      onSwitchChange(event.target.checked);
    }
  };

  return (
    <Box
      sx={{
        border: `1.5px solid ${themeColor}`,
        borderRadius: "20px",
        padding: "20px",
        textAlign: "left",
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
        height: "100%",
        minHeight: "185px",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        backgroundColor: cardBg,
        boxShadow: "none",
        transition: "all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
      }}
      onClick={!isSwitchable ? handleCardClick : undefined}
    >
      <Box>
        {/* Top row: Icon and Pin */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
          {/* Icon Container */}
          <Box
            sx={{
              width: "48px",
              height: "48px",
              borderRadius: "12px",
              backgroundColor: themeColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#ffffff",
            }}
          >
            {icon}
          </Box>

          {/* Pin icon button */}
          <IconButton
            onClick={onTogglePin}
            size="small"
            sx={{
              color: isPinned
                ? (theme.palette.mode === "dark" ? "#ffffff" : "#111827")
                : (theme.palette.mode === "dark" ? "rgba(255,255,255,0.3)" : "#C8D3E6"),
              transform: isPinned ? "rotate(45deg)" : "none",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
                color: isPinned 
                  ? (theme.palette.mode === "dark" ? "#ffffff" : "#111827")
                  : (theme.palette.mode === "dark" ? "rgba(255,255,255,0.6)" : "#111827"),
              }
            }}
          >
            {isPinned ? <PinnedIcon fontSize="small" /> : <UnpinnedIcon fontSize="small" />}
          </IconButton>
        </Box>

        {/* Middle row: Title & Description */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="h6"
            fontWeight="700"
            sx={{
              color: primaryTextColor,
              fontSize: "1.1rem",
              mb: 0.5,
              lineHeight: 1.3
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: secondaryTextColor,
              fontSize: "0.85rem",
              lineHeight: 1.4
            }}
          >
            {description}
          </Typography>
        </Box>
      </Box>

      {/* Bottom row: Switch/Spacing + Divider + Count */}
      <Box>
        {isSwitchable ? (
          <FormControlLabel
            control={
              <Switch
                checked={isOn}
                onChange={handleSwitchChange}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: themeColor,
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: themeColor,
                  },
                }}
              />
            }
            label=""
            labelPlacement="end"
            sx={{ ml: "auto", mr: 0, alignSelf: "flex-end", mb: 1 }}
          />
        ) : null}
        <Divider sx={{ my: 1.5, borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "#f0f0f0" }} />
        <Typography
          variant="caption"
          sx={{
            color: secondaryTextColor,
            fontWeight: "500",
            fontSize: "0.8rem",
            display: "block"
          }}
        >
          {itemCount} items
        </Typography>
      </Box>

      {disabled && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            height: "100%",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: alpha(cardBg, 0.7),
            borderRadius: "20px",
            zIndex: 1,
          }}
        >
          <LockIcon sx={{ color: secondaryTextColor }} fontSize="large" />
        </Box>
      )}
    </Box>
  );
};

const LOCAL_STORAGE_PIN_KEY = "mms_pinned_configurations";
const DEFAULT_PINNED = [
  "Packagings & Pricing",
  "Thumbnail Templates",
  "Proposal Cover Templates",
  "Roles and Permissions",
];

const Configurations: React.FC = () => {
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const { userPermissions } = usePermissions();
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  
  const [pinnedIds, setPinnedIds] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_PIN_KEY);
      return saved ? JSON.parse(saved) : DEFAULT_PINNED;
    } catch {
      return DEFAULT_PINNED;
    }
  });

  const primaryColor = reduxTheme.primary_colors?.[100] || "#724E41";
  const colors = tokens(theme.palette.mode, reduxTheme);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setActiveCategory(newValue);
  };

  const handleTogglePin = (title: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setPinnedIds((prev) => {
      const next = prev.includes(title)
        ? prev.filter((id) => id !== title)
        : [...prev, title];
      localStorage.setItem(LOCAL_STORAGE_PIN_KEY, JSON.stringify(next));
      return next;
    });
  };

  const allSettings = [
    {
      icon: <InventoryIcon sx={{ color: "#ffffff", fontSize: "24px" }} />,
      title: "Packagings & Pricing",
      description: "Manage funeral streaming packages (X, Pro, Lux)",
      navigateTo: "/configurations/package",
      category: "Bookings",
      themeColor: "#E67E22",
      itemCount: 3,
    },
    {
      icon: <ImageIcon sx={{ color: "#ffffff", fontSize: "24px" }} />,
      title: "Thumbnail Templates",
      description: "Add and manage your thumbnail designs",
      navigateTo: "/configurations/thumbnail-template-manager",
      category: "Streams",
      thumbnail_type: "youtube_thumbnail",
      themeColor: "#E91E63",
      itemCount: 8,
    },
    {
      icon: <A1Icon sx={{ color: "#ffffff", fontSize: "24px" }} />,
      title: "A1 Templates",
      description: "Add and manage your AI designs",
      navigateTo: "/configurations/thumbnail-template-manager",
      category: "Streams",
      thumbnail_type: "aone",
      themeColor: "#2F3640",
      itemCount: 3,
    },
    {
      icon: <JobsIcon sx={{ color: "#ffffff", fontSize: "24px" }} />,
      title: "Competency Question Templates",
      description: "Manage reusable competency test question sets per department",
      navigateTo: "/configurations/competency-question-templates",
      category: "Jobs",
      themeColor: "#614438",
      itemCount: 5,
    },
    {
      icon: <ShareIcon sx={{ color: "#ffffff", fontSize: "24px" }} />,
      title: "Share Message Templates",
      description: "Create and manage social media share templates",
      navigateTo: "/configurations/share-messages",
      category: "Streams",
      themeColor: "#00D2D3",
      itemCount: 5,
    },
    {
      icon: <FooterIcon sx={{ color: "#ffffff", fontSize: "24px" }} />,
      title: "Livestream Booking Agreement & Client Acknowledgement Form",
      description: "Edit the livestream booking agreement and its formatting",
      navigateTo: "/configurations/internet-agreement",
      category: "Streams",
      themeColor: "#00897B",
      itemCount: 1,
    },
    {
      icon: <CoverIcon sx={{ color: "#ffffff", fontSize: "24px" }} />,
      title: "Proposal Cover Templates",
      description: "Add and manage your cover page designs",
      navigateTo: "/configurations/proposal-cover-page",
      category: "Proposal",
      themeColor: "#6F42C1",
      itemCount: 6,
    },
    {
      icon: <LetterIcon sx={{ color: "#ffffff", fontSize: "24px" }} />,
      title: "Proposal Introduction Letter Templates",
      description: "Add and manage your introduction letters on proposals",
      navigateTo: "/configurations/proposal-introduction-letter",
      category: "Proposal",
      themeColor: "#FF7675",
      itemCount: 4,
    },
    {
      icon: <TestimonialsIcon sx={{ color: "#ffffff", fontSize: "24px" }} />,
      title: "Customer Testimonials",
      description: "Add and manage your customer testimonials",
      navigateTo: "/configurations/testimonials",
      category: "Proposal",
      themeColor: "#C5A880",
      itemCount: 12,
    },
    {
      icon: <ShieldIcon sx={{ color: "#ffffff", fontSize: "24px" }} />,
      title: "Roles and Permissions",
      description: "Control access levels across all departments",
      navigateTo: "/configurations/manage-roles",
      category: "User Management",
      themeColor: "#8D6E63",
      itemCount: 24,
    },
    {
      icon: <InventoryIcon sx={{ color: "#ffffff", fontSize: "24px" }} />,
      title: "Chema Pricing & Fees",
      description: "Platform fee, processing rates, withdrawal costs and donation amounts",
      navigateTo: "/configurations/chema-pricing",
      category: "Chema",
      themeColor: "#0D9488",
      itemCount: 1,
    },
    {
      icon: <SessionTimeoutIcon sx={{ color: "#ffffff", fontSize: "24px" }} />,
      title: "Session Timeout",
      description: "Set how long users stay logged in after their own login",
      navigateTo: "/configurations/session-settings",
      category: "User Management",
      themeColor: "#3F5F8A",
      itemCount: 1,
    },
    {
      icon: <ApiApplicationsIcon sx={{ color: "#ffffff", fontSize: "24px" }} />,
      title: "API Applications",
      description: "Manage service-account tokens for external integrations",
      navigateTo: "/configurations/api-applications",
      category: "User Management",
      themeColor: "#6C5CE7",
      itemCount: 0,
    },
    {
      icon: <SeoIcon sx={{ color: "#ffffff", fontSize: "24px" }} />,
      title: "SEO Management",
      description: "Configure metadata and keywords for all your pages",
      navigateTo: "/configurations/seo-management",
      category: "Seo Management",
      themeColor: "#2ED573",
      itemCount: 10,
    },
    {
      icon: <ComponentsIcon sx={{ color: "#ffffff", fontSize: "24px" }} />,
      title: "Venues Administration",
      description: "Manage venue dashboard widgets and display settings",
      navigateTo: "/configurations/venues-admin",
      category: "Components Management",
      themeColor: "#54A0FF",
      itemCount: 8,
    },
    {
      icon: <VisibilityIcon sx={{ color: "#ffffff", fontSize: "24px" }} />,
      title: "Venue Website Visibility",
      description: "Choose what everyone and signed-in visitors see on venue pages",
      navigateTo: "/configurations/venue-website-visibility",
      category: "Venues",
      themeColor: "#0E7C86",
      itemCount: 14,
    },
    {
      icon: <ComponentsIcon sx={{ color: "#ffffff", fontSize: "24px" }} />,
      title: "Components manager",
      description: "Manage components and their leads",
      navigateTo: "/configurations/components-manager",
      category: "Venues",
      themeColor: "#2F3542",
      itemCount: 15,
    },
    {
      icon: <YouTubeIcon sx={{ color: "#ffffff", fontSize: "24px" }} />,
      title: "Youtube Channels",
      description: "View all available youtube channels and reset access tokens",
      navigateTo: "/youtube-channels",
      category: "Streams",
      themeColor: "#FF4757",
      itemCount: 2,
    },
    {
      icon: <ComponentsIcon sx={{ color: "#ffffff", fontSize: "24px" }} />,
      title: "Dashboard Widgets",
      description: "Manage widgets displayed on Sales, Venues and other dashboards",
      navigateTo: "/configurations/dashboard-widgets",
      category: "Components Management",
      themeColor: "#1DD1A1",
      itemCount: 6,
    },
    {
      icon: <TvIcon sx={{ color: "#ffffff", fontSize: "24px" }} />,
      title: "TV Dashboard Templates",
      description: "Create TV dashboard templates and choose which one is live",
      navigateTo: "/configurations/tv-dashboard-templates",
      category: "Components Management",
      themeColor: "#5F27CD",
      itemCount: 4,
    },
    {
      icon: <FooterIcon sx={{ color: "#ffffff", fontSize: "24px" }} />,
      title: "Footer Customizer",
      description: "Manage app download routes, global links, and company contacts",
      navigateTo: "/configurations/footer-links",
      category: "Components Management",
      themeColor: "#8395A7",
      itemCount: 3,
    },
    {
      icon: <UpcomingIcon sx={{ color: "#ffffff", fontSize: "24px" }} />,
      title: "Upcoming Features Config",
      description: "Manage app download routes, global links, and company contacts",
      navigateTo: "/configurations/upcoming-features",
      category: "Components Management",
      themeColor: "#FFA502",
      itemCount: 5,
    },
    {
      icon: <Email sx={{ color: "#ffffff", fontSize: "24px" }} />,
      title: "Email Configurations",
      description: "Manage email templates, how they look like, and what triggers them",
      navigateTo: "/configurations/email-templates",
      category: "Components Management",
      themeColor: "#FF6B6B",
      itemCount: 4,
    },
    {
      icon: <NotificationIcon sx={{ color: "#ffffff", fontSize: "24px" }} />,
      title: "Notification Manager",
      description: "Manage notification templates, their targets, and delivery methods",
      navigateTo: "/configurations/notification-manager",
      category: "Components Management",
      themeColor: "#7158e2",
      itemCount: 5,
    },
  ];

  const categories = [
    { label: "All", value: "All" },
    { label: "Bookings", value: "Bookings" },
    { label: "Streams", value: "Streams" },
    { label: "Proposals", value: "Proposal" },
    { label: "Venues", value: "Venues" },
    { label: "User Management", value: "User Management" },
    { label: "SEO Management", value: "Seo Management" },
    { label: "Components Management", value: "Components Management" },
    { label: "Jobs", value: "Jobs" },
    { label: "Chema", value: "Chema" },
  ];

  if (!hasConfigurationAccess(userPermissions)) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h5" color="error">
          Access Denied. You do not have permission to view configurations.
        </Typography>
      </Box>
    );
  }

  // Filter settings by Category Tab and Search Query
  const filteredSettings = allSettings.filter((setting) => {
    const matchesCategory = activeCategory === "All" || setting.category === activeCategory;
    const matchesSearch =
      setting.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      setting.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const pinnedSettings = filteredSettings.filter((setting) => pinnedIds.includes(setting.title));
  const otherSettings = filteredSettings.filter((setting) => !pinnedIds.includes(setting.title));

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.mode === "dark" ? colors.primary[900] : "#ffffff",
        borderRadius: "24px",
        p: { xs: 2, md: 4 },
        boxShadow: theme.palette.mode === "dark" ? "0 4px 20px rgba(0,0,0,0.4)" : "0 4px 20px rgba(216,221,235,0.2)",
        minHeight: "100%",
      }}
    >
        {/* Search Bar */}
        <Box sx={{ mb: 3, maxWidth: "320px" }}>
          <TextField
            fullWidth
            placeholder="Search here"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant="outlined"
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon sx={{ color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.5)" : "#A4B1C5" }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: "20px",
                backgroundColor: theme.palette.mode === "dark" ? colors.primary[800] : "#F5F7FA",
                "& fieldset": { border: "none" },
                px: 1.5,
                height: "40px",
                fontSize: "0.9rem",
              }
            }}
          />
        </Box>

        {/* Categories Tabs */}
        <Tabs
          value={activeCategory}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            mb: 4,
            borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "#E4E9F2"}`,
            "& .MuiTabs-indicator": {
              backgroundColor: primaryColor,
              height: "3px",
              borderRadius: "3px 3px 0 0",
            },
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: "600",
              fontSize: "0.95rem",
              minWidth: "auto",
              px: { xs: 2, md: 3 },
              color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.6)" : "#64748B",
              "&.Mui-selected": {
                color: theme.palette.mode === "dark" ? "#ffffff" : "#111827",
              },
            },
          }}
        >
          {categories.map((category) => (
            <Tab key={category.value} label={category.label} value={category.value} />
          ))}
        </Tabs>

        {/* Pinned Section */}
        {pinnedSettings.length > 0 && (
          <Box sx={{ mb: 5 }}>
            <Box display="flex" alignItems="center" sx={{ mb: 3 }}>
              <Typography
                variant="h5"
                fontWeight="700"
                sx={{
                  color: theme.palette.mode === "dark" ? "#ffffff" : "#111827",
                  fontSize: "1.25rem",
                }}
              >
                Pinned
              </Typography>
              <PinnedIcon
                sx={{
                  ml: 1,
                  color: theme.palette.mode === "dark" ? "#ffffff" : "#111827",
                  transform: "rotate(45deg)",
                  fontSize: "1.2rem",
                }}
              />
            </Box>

            <Grid container spacing={3}>
              {pinnedSettings.map((setting, index) => (
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={setting.title}>
                  <MotionCard
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    whileHover={{
                      scale: 1.025,
                      y: -4,
                      boxShadow: theme.palette.mode === "dark" ? "0 8px 24px rgba(0,0,0,0.5)" : "0 8px 24px rgba(216,221,235,0.4)",
                      transition: { duration: 0.2 },
                    }}
                  >
                    <SettingsCard
                      {...setting}
                      isPinned={true}
                      onTogglePin={(e) => handleTogglePin(setting.title, e)}
                    />
                  </MotionCard>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Others Section */}
        {otherSettings.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="h5"
              fontWeight="700"
              sx={{
                mb: 3,
                color: theme.palette.mode === "dark" ? "#ffffff" : "#111827",
                fontSize: "1.25rem",
              }}
            >
              Others
            </Typography>

            <Grid container spacing={3}>
              {otherSettings.map((setting, index) => (
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={setting.title}>
                  <MotionCard
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    whileHover={{
                      scale: 1.025,
                      y: -4,
                      boxShadow: theme.palette.mode === "dark" ? "0 8px 24px rgba(0,0,0,0.5)" : "0 8px 24px rgba(216,221,235,0.4)",
                      transition: { duration: 0.2 },
                    }}
                  >
                    <SettingsCard
                      {...setting}
                      isPinned={false}
                      onTogglePin={(e) => handleTogglePin(setting.title, e)}
                    />
                  </MotionCard>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Empty State */}
        {filteredSettings.length === 0 && (
          <Box sx={{ py: 8, textAlign: "center" }}>
            <Typography variant="h6" sx={{ color: "text.secondary", fontWeight: "500" }}>
              No configurations found matching &quot;{searchQuery}&quot;
            </Typography>
          </Box>
        )}
      </Box>
  );
};

export default Configurations;
